import React, { Component } from 'react';
import SideMenu from '../admin/SideMenu';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import TopMenu from '../admin/TopBar';
import { appDatabasePrimaryFunctions, base } from '../../base';
import '../../styles/css/AdminMain.css';
import { Toast, ErrorMessage, WarningMessage } from '../utils/HelpfulFunction';
import SideMenuPreview from "../utils/PreviewMenu";
import { experiencePath, getScope, subscribeToScope, registerScopeGuard } from '../utils/experienceScope';

// Firebase rejects the WHOLE update() if any single value in it is undefined,
// so one legacy item with a missing field fails the entire save rather than
// just itself. Nothing here needs to write undefined: an absent key and an
// undefined one mean the same thing to RTDB.
function withoutUndefined(obj) {
    const clean = {};
    Object.keys(obj).forEach(key => {
        if (obj[key] !== undefined) clean[key] = obj[key];
    });
    return clean;
}

class SetUpSideMenu extends Component {
    constructor(props) {
        super(props);
        this.state = {
            itemsList: [],
            loading: true,
            icon: '',
            backgroundImage: '',
            localItemsList: [],
            hasUnsavedChanges: false, // New state property to track unsaved changes
            // The working scope, owned by utils/experienceScope: a ?experience=
            // in the URL wins, else this admin's stored choice, else '' for the
            // default experience. The top-bar switcher drives it.
            selectedExperience: getScope()
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.toggle = this.toggle.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.saveLocalStateToDatabase = this.saveLocalStateToDatabase.bind(this);
        this.handleLocalChange = this.handleLocalChange.bind(this);
        this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
        this.bindData = this.bindData.bind(this);
        this.handleExperienceChange = this.handleExperienceChange.bind(this);
    }

    async componentDidMount() {
        this.unsubscribeScope = subscribeToScope(id => this.handleExperienceChange(id));
        this.unregisterScopeGuard = registerScopeGuard(() => !this.state.hasUnsavedChanges);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        await this.bindData();
    }

    async bindData() {
        const { selectedExperience } = this.state;
        this.sideMenuOptionsRef = base.listenTo(experiencePath(selectedExperience, 'sideMenuOptions'), {
            context: this,
            state: 'itemsList',
            query: {
                orderByChild: 'order'
            },
            asArray: true,
            then(data = []) {
                this.setState({
                    loading: false,
                    itemsList: data,
                    localItemsList: data // Initialize localItemsList with database data
                });
            },
            catch(e) {
                this.setState({ loading: false });
            }
        });

        const backgroundImage = await appDatabasePrimaryFunctions.ref(experiencePath(selectedExperience, 'settings/backgroundImage')).once('value');
        if (backgroundImage.exists()) {
            this.setState({
                backgroundImage: backgroundImage.val()
            });
        }
    }

    handleExperienceChange(experienceId) {
        // Detach the current experience's binding before re-binding to the new one
        base.removeBinding(this.sideMenuOptionsRef);
        this.setState({
            selectedExperience: experienceId,
            itemsList: [],
            localItemsList: [],
            backgroundImage: '',
            hasUnsavedChanges: false,
            loading: true
        }, () => this.bindData());
    }

    componentWillUnmount() {
        if (this.unsubscribeScope) this.unsubscribeScope();
        if (this.unregisterScopeGuard) this.unregisterScopeGuard();
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        base.removeBinding(this.sideMenuOptionsRef);
    }

    handleBeforeUnload(event) {
        if (this.state.hasUnsavedChanges) {
            const message = 'You have unsaved changes. Are you sure you want to leave?';
            event.returnValue = message; // Standard way to trigger a confirmation dialog in modern browsers
            return message;
        }
    }

    handleChange(event) {
        let target = event.target;
        let value = target.type === 'checkbox' ? target.checked : target.value;
        this.setState({ [target.name]: value });
    }

    handleLocalChange(event) {
        let target = event.target;
        let value = target.type === 'checkbox' ? target.checked : target.value;
        this.setState({ [target.name]: value });
    }

    handleSubmit(event) {
        event.preventDefault();
        if (this.state.loading) {
            return;
        }

        const name = this.state.itemName || "";
        const itemLink = this.state.itemLink || "";
        const itemsList = this.state.localItemsList || [];
        const buttonType = this.state.buttonType;
        const openOutsideApp = this.state.openOutsideApp;
        if (!name || name.length > 50) {
            ErrorMessage.fire({
                title: 'Oh uh!',
                text: 'Name cannot be longer than 50 characters or be left blank!',
            });
            return;
        }
        const icon = this.state.icon || "";
        if (!name) {
            ErrorMessage.fire({
                title: 'Not all fields filled out!',
                text: 'Please enter them and try again'
            });
            return;
        }
        // Validate screen selection for isScreen button type
        if (buttonType === 'isScreen' && !this.state.screen) {
            ErrorMessage.fire({
                title: 'Screen not selected!',
                text: 'Please select a screen to navigate to.',
            });
            return;
        }

        const itemToSave = {};
        itemToSave['header'] = name;
        itemToSave['link'] = itemLink;
        itemToSave['icon'] = icon;
        // Items created before these fields existed read back as undefined,
        // which Firebase refuses to store.
        itemToSave['buttonType'] = buttonType || 'isWebsiteLink';
        itemToSave['openOutsideApp'] = openOutsideApp || false;
        itemToSave['screen'] = this.state.screen || null;
        itemToSave['order'] = (this.state.itemToEdit && this.state.itemToEdit.order) || itemsList.length + 1;
        this.setState({ loading: true });
        itemToSave['id'] = (this.state.itemToEdit && (this.state.itemToEdit.key || this.state.itemToEdit.id)) || appDatabasePrimaryFunctions.ref().push().key;

        const updatedItemsList = [...itemsList];
        if (this.state.editingItem) {
            const index = updatedItemsList.findIndex(item => item.id === this.state.itemToEdit.id);
            updatedItemsList[index] = itemToSave;
        } else {
            updatedItemsList.push(itemToSave);
        }

        this.setState({
            localItemsList: updatedItemsList,
            itemName: "",
            itemLink: "",
            itemToEdit: null,
            editingItem: false,
            openOutsideApp: false,
            screen: '',
            modal: false,
            loading: false,
            hasUnsavedChanges: true // Set hasUnsavedChanges to true when local changes are made
        });

        Toast.fire({
            title: this.state.itemToEdit ? 'Edited Locally!' : 'Created Locally!'
        });
    }

    editItem(event) {
        event.preventDefault();
        const array = this.state.localItemsList;
        const item = array[event.currentTarget.value];
        this.setState({
            modal: true,
            itemName: item.header,
            itemLink: item.link,
            itemToEdit: item,
            icon: item.icon,
            editingItem: true,
            buttonType: item.buttonType || 'isWebsiteLink',
            openOutsideApp: item.openOutsideApp || false,
            screen: item.screen || ''
        });
    }

    async deleteItem(e) {
        e.preventDefault();
        // Read the row index off the button now: currentTarget is null once the
        // handler returns, and target may be the icon inside the button, which
        // carries no value at all.
        const indexToDelete = parseInt(e.currentTarget.value, 10);
        if (isNaN(indexToDelete)) return;
        const response = await WarningMessage.fire({
            title: 'Delete?',
            text: 'Are you sure you want to do this? You will no longer be able to use this anymore',
            confirmButtonText: 'Delete'
        });
        if (response.value) {
            const updatedItemsList = this.state.localItemsList.filter((item, idx) => idx !== indexToDelete);
            this.setState({ localItemsList: updatedItemsList, hasUnsavedChanges: true }); // Set hasUnsavedChanges to true when local changes are made
            Toast.fire({
                title: 'Deleted Locally!'
            });
        }
    }

    toggle() {
        this.setState({
            modal: !this.state.modal,
            itemName: "",
            itemLink: "",
            icon: '',
            itemToEdit: null,
            editingItem: false,
            buttonType: 'isWebsiteLink',
            disableRadioButtons: false,
            openOutsideApp: false,
            screen: ''
        });
    }

    async changeOrder(item, direction, index) {
        let itemsListCopyArray = this.state.localItemsList || [];
        let originalOrder = item.order || index;
        const itemId = item.id || item.key;

        // Check if the order needs to be changed
        if ((originalOrder === 1 && direction === 1) || (originalOrder === itemsListCopyArray.length && direction === -1)) {
            return;
        }

        let newOrder = originalOrder - direction;
        const itemListCopyObject = {};

        // Update orders based on direction
        for (let i in itemsListCopyArray) {
            let currentLocationInArray = parseInt(i) + 1;
            let itemIterated = itemsListCopyArray[i] || {};
            let itemIteratedId = itemIterated.id || itemIterated.key;

            if (newOrder === itemIterated.order) {
                itemIterated.order = originalOrder;
            } else if (itemId === itemIteratedId) {
                itemIterated.order = newOrder;
            } else if (!itemIterated.order && currentLocationInArray === newOrder) {
                itemIterated.order = originalOrder;
            } else if (!itemIterated.order) {
                itemIterated.order = currentLocationInArray;
            }

            itemListCopyObject[itemIteratedId] = itemIterated;
            itemListCopyObject[itemIteratedId]['key'] = null;
            itemListCopyObject[itemIteratedId]['id'] = itemIteratedId;
        }

        // Reorder items if there are duplicates or if the ordering doesn't start from 1
        let orderSet = new Set();
        for (let item of itemsListCopyArray) {
            if (item.order) {
                orderSet.add(item.order);
            }
        }

        if (orderSet.size !== itemsListCopyArray.length || !orderSet.has(1)) {
            itemsListCopyArray.sort((a, b) => (a.order || 0) - (b.order || 0));
            itemsListCopyArray.forEach((item, idx) => {
                item.order = idx + 1;
                itemListCopyObject[item.id || item.key].order = idx + 1;
            });
        }

        this.setState({ localItemsList: itemsListCopyArray, hasUnsavedChanges: true }); // Set hasUnsavedChanges to true when local changes are made
    }

    saveLocalStateToDatabase() {
        const { localItemsList, selectedExperience } = this.state;

        // An empty list is a legitimate state to save: it means every item was
        // deleted. The diff below removes whatever the database still holds.

        // Create an object for items to be updated in the database
        const itemListCopyObject = {};
        localItemsList.forEach(item => {
            itemListCopyObject[item.id || item.key] = withoutUndefined({ ...item, key: null, id: item.id || item.key });
        });

        // Retrieve the existing items in the database
        appDatabasePrimaryFunctions.ref(experiencePath(selectedExperience, 'sideMenuOptions')).once('value')
            .then(snapshot => {
                const existingItems = snapshot.val() || {};
                const itemsToDelete = [];

                // Identify items that need to be deleted (those in the database but not in localItemsList)
                Object.keys(existingItems).forEach(id => {
                    if (!itemListCopyObject[id]) {
                        itemsToDelete.push(id);
                    }
                });

                // Remove items that are not present in localItemsList
                const deletePromises = itemsToDelete.map(id =>
                    appDatabasePrimaryFunctions.ref(`${experiencePath(selectedExperience, 'sideMenuOptions')}/${id}`).remove()
                );

                // Update the database with the new items and handle deletions
                return Promise.all(deletePromises)
                    .then(() => Object.keys(itemListCopyObject).length === 0
                        ? null
                        : appDatabasePrimaryFunctions.ref(experiencePath(selectedExperience, 'sideMenuOptions')).update(itemListCopyObject));
            })
            .then(() => {
                Toast.fire({
                    title: 'Saved Successfully!'
                });
                this.setState({ hasUnsavedChanges: false }); // Set hasUnsavedChanges to false when changes are saved
            })
            .catch(err => {
                ErrorMessage.fire({
                    title: 'There was some error!',
                    text: 'Try again and if the problem persists, try logging out and logging back in.'
                });
                console.log(err);
            });
    }

    render() {
        const { loading, disableRadioButtons = false, localItemsList, hasUnsavedChanges } = this.state;

        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: loading ? 'block' : 'none' }} />
                <SideMenu />
                <TopMenu />
                <div className="admin-main-panel">
                    <div className="admin-form-box">
                        <div>
                            <div className="admin-page-head" style={{ margin: 0 }}>
                                <p className="admin-eyebrow">App layout</p>
                                <p className="admin-header-text">Side Menu Buttons</p>
                                <p className="admin-page-sub">The items in your app&rsquo;s side menu, in the order fans see them.</p>
                            </div>


                            <div className="admin-actions">
                                <button className="btn btn-primary" onClick={this.saveLocalStateToDatabase}>Save changes</button>
                                <button className="btn create-prize-button" onClick={this.toggle}>Add item</button>
                                {hasUnsavedChanges && (
                                    <span className="admin-unsaved">You have unsaved changes</span>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-md-8">
                                    <ul className="admin-rowlist">
                                        {
                                            localItemsList.sort((a, b) => a.order > b.order ? 1 : -1).map((item, i) => {
                                                let orderNumber = item.order || i + 1;
                                                return (
                                                    <li key={i} className="admin-rowitem">
                                                        <span className="admin-rowitem-ord">{orderNumber}</span>

                                                        <div className="admin-rowitem-main">
                                                            <p className="admin-rowitem-name">{item.header}</p>
                                                            {item.buttonType === 'isWebsiteLink' &&
                                                                <p className="admin-rowitem-meta" title={item.link}>{item.link}</p>}
                                                            {item.buttonType === 'isScreen' &&
                                                                <p className="admin-rowitem-meta">Screen: {item.screen}</p>}
                                                        </div>

                                                        {/* was two 40px emoji with no disabled state or label */}
                                                        {localItemsList.length > 1 && (
                                                            <span className="admin-rowitem-order">
                                                                <button
                                                                    type="button"
                                                                    className="admin-order-btn"
                                                                    aria-label={`Move ${item.header} up`}
                                                                    disabled={orderNumber <= 1}
                                                                    onClick={() => this.changeOrder(item, 1, i + 1)}
                                                                >
                                                                    <span className="fa fa-chevron-up"/>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="admin-order-btn"
                                                                    aria-label={`Move ${item.header} down`}
                                                                    disabled={localItemsList.length <= i + 1}
                                                                    onClick={() => this.changeOrder(item, -1, i + 1)}
                                                                >
                                                                    <span className="fa fa-chevron-down"/>
                                                                </button>
                                                            </span>
                                                        )}

                                                        <span className="admin-row-actions">
                                                            <button type="button" className="admin-icon-btn" aria-label="Edit item" title="Edit item" onClick={this.editItem.bind(this)} value={i}><span className="fa fa-pencil" aria-hidden="true"/></button>
                                                            <button type="button" className="admin-icon-btn is-danger" aria-label="Delete item" title="Delete item" onClick={this.deleteItem.bind(this)} value={i}><span className="fa fa-trash-o" aria-hidden="true"/></button>
                                                        </span>
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                </div>
                                <div className="col-md-4">
                                    <SideMenuPreview backgroundImage={this.state.backgroundImage} items={localItemsList} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Modal isOpen={this.state.modal} toggle={this.toggle} style={{ width: '90%' }} id="myModal">
                    <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }} />
                    <ModalHeader toggle={this.toggle}>Add Item</ModalHeader>
                    <ModalBody>
                        <div className="container-out">
                            <div className="question-box question-form">
                                <form className="pl-3 pr-3" onSubmit={this.handleSubmit} id="create-email-form">
                                    <div className="form-group">
                                        <label htmlFor="itemName">Name</label>
                                        {disableRadioButtons &&
                                            <p className="admin-hint">
                                                This is a required app-store item. Renaming it can affect app review, so
                                                keep it recognisable.
                                            </p>
                                        }
                                        <input id="itemName" name="itemName" type="text" className="form-control" value={this.state.itemName} onChange={this.handleLocalChange} placeholder="Name" />
                                    </div>
                                    <div className="row">
                                        {/*{process.env.REACT_APP_HAS_PRIZES === "true" && (*/}
                                            <>
                                                <div className="col-12 mb-3">
                                                    <div className="row">
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isWebsiteLink"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isWebsiteLink"
                                                                    checked={this.state.buttonType === "isWebsiteLink"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isWebsiteLink">
                                                                    Website Link
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isSupportLink"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isSupportLink"
                                                                    checked={this.state.buttonType === "isSupportLink"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isSupportLink">
                                                                    Open Support
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isProfile"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isProfile"
                                                                    checked={this.state.buttonType === "isProfile"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isProfile">
                                                                    My Profile
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isEditUserInfo"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isEditUserInfo"
                                                                    checked={this.state.buttonType === "isEditUserInfo"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isEditUserInfo">
                                                                    Edit User Info
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isSectionTitle"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isSectionTitle"
                                                                    checked={this.state.buttonType === "isSectionTitle"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isSectionTitle">
                                                                    Is Section Title
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isLogOut"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isLogOut"
                                                                    checked={this.state.buttonType === "isLogOut"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isLogOut">
                                                                    Is Log Out
                                                                </label>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isDeleteButton"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isDeleteButton"
                                                                    checked={this.state.buttonType === "isDeleteButton"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isLogOut">
                                                                    Delete Account
                                                                </label>
                                                            </div>
                                                        </div>
                                                        {process.env.REACT_APP_HAS_TICKETMASTER === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="logOutTicketMaster"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="logOutTicketMaster"
                                                                        checked={this.state.buttonType === "logOutTicketMaster"}
                                                                        onChange={this.handleLocalChange}
                                                                        disabled={disableRadioButtons}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="logOutTicketMaster">
                                                                        Logout Ticketmaster SDK Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {process.env.REACT_APP_HAS_TICKETMASTER === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="isTicketMaster"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="isTicketMaster"
                                                                        checked={this.state.buttonType === "isTicketMaster"}
                                                                        onChange={this.handleLocalChange}
                                                                        disabled={disableRadioButtons}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="isTicketMaster">
                                                                        Ticketmaster SDK Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {process.env.REACT_APP_HAS_PLAYER_PICK === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="isPlayerPick"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="isPlayerPick"
                                                                        checked={this.state.buttonType === "isPlayerPick"}
                                                                        onChange={this.handleLocalChange}
                                                                        disabled={disableRadioButtons}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="isPlayerPick">
                                                                        Player Pick Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-12 mb-3">
                                                    <div className="row">
                                                        {process.env.REACT_APP_HAS_BADGES === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="isBadges"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="isBadges"
                                                                        checked={this.state.buttonType === "isBadges"}
                                                                        onChange={this.handleLocalChange}
                                                                        disabled={disableRadioButtons}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="isBadges">
                                                                        Badges Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {process.env.REACT_APP_HAS_CHECK_IN === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="isCheckIn"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="isCheckIn"
                                                                        checked={this.state.buttonType === "isCheckIn"}
                                                                        onChange={this.handleLocalChange}
                                                                        disabled={disableRadioButtons}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="isCheckIn">
                                                                        Check In Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {process.env.REACT_APP_HAS_PRIZES === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="isPrizes"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="isPrizes"
                                                                        checked={this.state.buttonType === "isPrizes"}
                                                                        onChange={this.handleLocalChange}
                                                                        disabled={disableRadioButtons}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="isPrizes">
                                                                        Reward Store Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-12 mb-3">
                                                    <div className="row">
                                                        {process.env.REACT_APP_HAS_RADIO === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="isRadio"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="isRadio"
                                                                        checked={this.state.buttonType === "isRadio"}
                                                                        onChange={this.handleLocalChange}
                                                                        disabled={disableRadioButtons}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="isRadio">
                                                                        Radio Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {process.env.REACT_APP_HAS_CUE_AUDIO === "true" && (
                                                            <div className="col-md-4">
                                                                <div className="form-check">
                                                                    <input
                                                                        id="isCueAudio"
                                                                        name="buttonType"
                                                                        type="radio"
                                                                        className="form-check-input"
                                                                        value="isCueAudio"
                                                                        checked={this.state.buttonType === "isCueAudio"}
                                                                        onChange={this.handleChange}
                                                                    />
                                                                    <label className="form-check-label" htmlFor="isCueAudio">
                                                                        Cue Audio Button
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="col-md-4">
                                                            <div className="form-check">
                                                                <input
                                                                    id="isScreen"
                                                                    name="buttonType"
                                                                    type="radio"
                                                                    className="form-check-input"
                                                                    value="isScreen"
                                                                    checked={this.state.buttonType === "isScreen"}
                                                                    onChange={this.handleLocalChange}
                                                                    disabled={disableRadioButtons}
                                                                />
                                                                <label className="form-check-label" htmlFor="isScreen">
                                                                    Navigate to Screen
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        {/*)}*/}
                                        {this.state.buttonType === "isWebsiteLink" && (
                                            <>
                                                <div className="col-12 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="itemLink">Link:</label>
                                                        <input
                                                            id="itemLink"
                                                            name="itemLink"
                                                            type="url"
                                                            className="form-control"
                                                            value={this.state.itemLink}
                                                            onChange={this.handleLocalChange}
                                                            placeholder="https://linktogoto.com"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="form-check">
                                                        <input
                                                            id="openOutsideApp"
                                                            name="openOutsideApp"
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            value="openOutsideApp"
                                                            checked={this.state.openOutsideApp}
                                                            onChange={this.handleLocalChange}
                                                        />
                                                        <label className="form-check-label" htmlFor="openOutsideApp">
                                                            Open Outside App
                                                        </label>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {this.state.buttonType === "isScreen" && (
                                            <div className="col-12 mb-3">
                                                <div className="form-group">
                                                    <label htmlFor="screen">Select Screen:</label>
                                                    <select
                                                        id="screen"
                                                        name="screen"
                                                        className="form-control"
                                                        value={this.state.screen || ""}
                                                        onChange={this.handleLocalChange}
                                                    >
                                                        <option value="">-- Select a Screen --</option>
                                                        <option value="Home">Home</option>
                                                        <option value="Schedule">Schedule</option>
                                                        <option value="Roster">Roster</option>
                                                        <option value="Stats">Stats</option>
                                                        <option value="Tickets">Tickets</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="admin-modal-actions">
                                        <button type="button" className="btn btn-secondary" onClick={this.toggle}>Cancel</button>
                                        <button className="btn btn-primary submit-button" id="submitButton">
                                            {this.state.editingItem ? 'Save changes' : 'Add item'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </ModalBody>
                </Modal>
            </div>
        );
    }
}

export default SetUpSideMenu;
